import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { toast } from "react-toastify";

const initialState = {
	list: [],
	projectTypes: [],
	loading: false,
	typesLoading: false,
	error: null,
};

export const loadProjectSiteDetails = createAsyncThunk(
	"projectSiteDetails/loadAll",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(`projectSite/ProjectSiteDetails`);
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load project site details",
				}
			);
		}
	}
);

export const loadProjectTypes = createAsyncThunk(
	"projectSiteDetails/loadProjectTypes",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await axios.get(`projects/ProjectTypeGetbyCompanyId`);
			return Array.isArray(data) ? data : [];
		} catch (error) {
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to load project types",
				}
			);
		}
	}
);

export const saveProjectSiteDetail = createAsyncThunk(
	"projectSiteDetails/saveSingle",
	async (payload, { rejectWithValue }) => {
		const { ProjectSiteId } = payload || {};
		try {
			const response = await axios.post(
				`projectSite/ProjectSiteDetailsSetup`,
				{
					...payload,
					ProjectSiteId: ProjectSiteId || 0,
					DataStatus: payload?.DataStatus ?? 1,
				}
			);
			toast.success(
				ProjectSiteId && ProjectSiteId !== 0
					? "Project site updated successfully"
					: "Project site added successfully"
			);
			return response?.data;
		} catch (error) {
			toast.error("Failed to save project site detail");
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to save project site detail",
				}
			);
		}
	}
);

export const deleteProjectSiteDetail = createAsyncThunk(
	"projectSiteDetails/deleteSingle",
	async (payload, { rejectWithValue }) => {
		const { ProjectSiteId } = payload || {};
		try {
			const response = await axios.post(`projectSite/ProjectSiteDetailsSetup`, {
				...payload,
				ProjectSiteId,
				DataStatus: 2,
			});
			toast.success("Project site deleted successfully");
			return {
				data: response?.data,
				ProjectSiteId,
			};
		} catch (error) {
			toast.error("Failed to delete project site detail");
			return rejectWithValue(
				error?.response?.data ?? {
					message: error?.message || "Unable to delete project site detail",
				}
			);
		}
	}
);

const projectSiteDetailsSlice = createSlice({
	name: "projectSiteDetails",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(loadProjectSiteDetails.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(loadProjectSiteDetails.fulfilled, (state, action) => {
				state.loading = false;
				state.list = action.payload ?? [];
			})
			.addCase(loadProjectSiteDetails.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to load project site details";
			})
			.addCase(saveProjectSiteDetail.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(saveProjectSiteDetail.fulfilled, (state) => {
				state.loading = false;
			})
			.addCase(saveProjectSiteDetail.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to save project site detail";
			})
			.addCase(deleteProjectSiteDetail.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteProjectSiteDetail.fulfilled, (state, action) => {
				state.loading = false;
				const deletedId = action.payload?.ProjectSiteId;
				if (deletedId) {
					state.list = state.list.filter(
						(item) => Number(item.ProjectSiteId) !== Number(deletedId)
					);
				}
			})
			.addCase(deleteProjectSiteDetail.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload?.message || "Unable to delete project site detail";
			})
			.addCase(loadProjectTypes.pending, (state) => {
				state.typesLoading = true;
				state.error = null;
			})
			.addCase(loadProjectTypes.fulfilled, (state, action) => {
				state.typesLoading = false;
				state.projectTypes = action.payload ?? [];
			})
			.addCase(loadProjectTypes.rejected, (state, action) => {
				state.typesLoading = false;
				state.error = action.payload?.message || "Unable to load project types";
			});
	},
});

export default projectSiteDetailsSlice.reducer;

